import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption
} from '@reach/combobox'
import { SearchIcon } from '@heroicons/react/outline'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect, ChangeEvent } from 'react'
import SmartyStreetsSDK, { core } from 'smartystreets-javascript-sdk'

import { Address } from '@models/address'
import styles from './Combobox.module.css'
import { formatAddress } from '@utils/format-address'

interface AddressSearchProps {
  address?: Address
  onSelect: (address: Address) => void
  onSelectMissing: () => void
  suggestMissing?: boolean
  clearAddress: () => void
  setTouched?: () => void
  name?: string
  isErrored?: boolean
  isLoading?: boolean
}

const AnimatedPopover = motion.custom(ComboboxPopover)
const AnimatedList = motion.custom(ComboboxList)

const popoverVariants = {
  hidden: {
    opacity: 0,
    y: -10
  },
  visible: {
    opacity: 1,
    y: 0
  }
}

export function AddressSearch(props: AddressSearchProps) {
  const [query, setQuery] = useState(formatAddress(props.address))
  const [suggestions, setSuggestions] = useState<
    SmartyStreetsSDK.usAutocompletePro.Suggestion[]
  >([])
  const [smartystreets] = useState(() => {
    const credentials = new core.SharedCredentials(
      process.env.NEXT_PUBLIC_SMARTYSTREETS_API_KEY ?? ''
    )

    return core.buildClient.usAutocompletePro(credentials)
  })

  function handleQueryChange(event: ChangeEvent<HTMLInputElement>) {
    props.clearAddress()
    setQuery(event.currentTarget.value)
  }

  useEffect(() => {
    if (props.address) {
      setQuery(formatAddress(props.address))
    }
  }, [props.address])

  useEffect(() => {
    if (query.trim() !== '') {
      const lookup = new SmartyStreetsSDK.usAutocompletePro.Lookup(query)

      smartystreets
        .send(lookup)
        .then(({ result }) => {
          setSuggestions(result ?? [])
        })
        .catch(() => {})
    }
  }, [query])

  function handleSelect(option: string) {
    if (option.trim()) {
      const [street, city, stateAndZip] = option.split(', ')
      const [state, zipCode] = stateAndZip.split(' ')
      const address: Address = {
        street,
        city,
        state,
        zipCode
      }
      const formattedAddress = formatAddress(address)

      setQuery(formattedAddress)
      props.onSelect(address)
    }
  }

  return (
    <Combobox openOnFocus onSelect={handleSelect}>
      {({ isExpanded }) => (
        <>
          <div className="flex focus-within:ring focus-within:ring-blue-100 rounded">
            <div
              className={`flex justify-center items-center px-4 rounded-l border border-r-0 ${
                props.isErrored ? styles.errorButton : styles.button
              }`}
            >
              {props.isLoading ? (
                <svg
                  className="animate-spin h-6 w-6 text-brand"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <SearchIcon width={24} height={24} />
              )}
            </div>
            <ComboboxInput
              id="addressSearch"
              name={props.name}
              value={query}
              autocomplete={false}
              selectOnClick
              placeholder="Search for an address..."
              onBlur={props.setTouched}
              onChange={handleQueryChange}
              className={`w-full p-4 rounded-r border border-l-0 focus:outline-none ${
                props.isErrored ? styles.errorInput : styles.input
              }`}
            />
          </div>
          <AnimatePresence>
            {isExpanded && (
              <AnimatedPopover
                variants={popoverVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                className={styles.popover}
              >
                <AnimatedList
                  persistSelection
                  className="divide-y divide-gray-100 rounded-md"
                >
                  {suggestions.map(
                    ({ streetLine, secondary, city, state, zipcode }) => {
                      const address = `${streetLine}, ${city}, ${state} ${zipcode}`
                      const key = `${streetLine}-${secondary}-${city}-${state}-${zipcode}`

                      return (
                        <ComboboxOption
                          value={address}
                          key={key}
                          className={styles.option}
                        >
                          {address}
                        </ComboboxOption>
                      )
                    }
                  )}
                  {suggestions.length < 10 && (
                    <ComboboxOption
                      value=""
                      className={styles.missingOption}
                      onClick={e => {
                        props.onSelectMissing()
                      }}
                    >
                      Can't find your address?
                    </ComboboxOption>
                  )}
                </AnimatedList>
              </AnimatedPopover>
            )}
          </AnimatePresence>
        </>
      )}
    </Combobox>
  )
}
